// Styles
import '@design/normalize.css'

// Vue core
import Vue from 'vue'

// App core
import App from './app'
import router from '@router'
import store from '@state/store'
import '@components/_globals'
import './registerServiceWorker'

// Services
import messageService from '@services/message-service'
import analyticsService from '@services/analytics-service'
import consentService from '@services/consent-service'
import createKeyControlsService from '@services/key-controls-service'
import createPlayerService from '@services/player-service'
import createQueueService from '@services/queue-service'
import { keyControlsServiceKey } from '@composables/use-key-controls-service'
import { playerServiceKey } from '@composables/use-player-service'
import { queueServiceKey } from '@composables/use-queue-service'
import { analyticsKey } from '@composables/use-analytics'
import { messagesKey } from '@composables/use-messages'

// Analytics
import mixpanelService from '@services/mixpanel-service'
import superPropertiesManager from '@services/mixpanel/super-properties-manager'
import appLoadTracker from '@services/mixpanel/app-load-tracker'
import playbackTracker from '@services/mixpanel/playback-tracker'

// Directives
import clickOutside from '@directives/clickOutside'
import safeHtml from '@directives/safeHtml'

// Global Configuration
Vue.config.productionTip = import.meta.env.PROD

const playerService = createPlayerService()
const queueService = createQueueService({ playerService })
const keyControlsService = createKeyControlsService(playerService, queueService)

// Player Service Setup
playerService.setVolume(store.state.settings.volume)
playerService.on('settingsUpdate', val => store.commit('settings/SET_SETTINGS', val))

// Mixpanel Analytics
const mixpanelToken = import.meta.env.VITE_MIXPANEL_TOKEN
if (mixpanelToken) {
    mixpanelService.init(mixpanelToken, {
        debug: import.meta.env.DEV,
        persistence: 'localStorage',
        track_pageview: false,
        batch_requests: true,
        opt_out_tracking_by_default: true,
    })

    consentService.restoreConsentState()

    // Auto-grant consent for logged-in users without consent (grandfathered)
    if (!consentService.hasConsent()) {
        const currentUser = store.state.auth.currentUser
        if (currentUser) {
            consentService.grantConsent('grandfathered')
        }
    }

    superPropertiesManager.initialize()

    const currentUser = store.state.auth.currentUser
    if (currentUser) {
        const memberId = currentUser.members_id?.id ? String(currentUser.members_id.id) : null
        if (memberId) {
            superPropertiesManager.updateUserStatus(true, memberId)
            mixpanelService.identify(memberId)
        }
    }

    appLoadTracker.start()

    // Report any listening from a session that crashed without a clean end (once, at boot).
    playbackTracker.recoverJournaledSession()

    playbackTracker.registerLifecycleListeners()
}

// Vue Directives
Vue.directive('click-outside', clickOutside)
Vue.directive('safe-html', safeHtml)

// App Initialization
const app = new Vue({
    router,
    store,
    provide: {
        [keyControlsServiceKey]: keyControlsService,
        [playerServiceKey]: playerService,
        [queueServiceKey]: queueService,
        [analyticsKey]: analyticsService,
        [messagesKey]: messageService,
    },
    render: h => h(App),
}).$mount('#app')

// Cypress Testing Support
if (window.Cypress) {
    window.__app__ = app
}
